import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  Input,
  OnInit,
  signal,
  Signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { AutocompleteComponent, InputBaseComponent } from '@libs/form/components';
import { AutocompleteInputModel, AutocompleteOptionModel, InputValidationErrors, InputValueType } from '@libs/form/models';
import { CatalogSearchRepositoryService, PricesFormatter } from '@libs/shared/services';
import { debounceTime, distinctUntilChanged, filter, map, Observable, of, shareReplay, switchMap } from 'rxjs';

@Component({
  selector: 'ui-search-suggestion-autocomplete',
  styleUrls: ['./search-suggestion-autocomplete.component.scss'],
  templateUrl: './search-suggestion-autocomplete.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AutocompleteComponent,
    ReactiveFormsModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => SearchSuggestionAutocompleteComponent),
    },
  ],
})
export class SearchSuggestionAutocompleteComponent extends InputBaseComponent implements ControlValueAccessor, OnInit, AfterViewInit {
  @Input()
  public data!: AutocompleteInputModel;

  @ViewChild(AutocompleteComponent, { static: true })
  public override valueAccessor!: AutocompleteComponent;

  public autocompleteOptions: WritableSignal<AutocompleteOptionModel[]> = signal([]);

  public autocompleteConfig: Signal<AutocompleteInputModel> = computed(() => {
    return {
      ...this.data,
      value: this.control.value as InputValueType,
      options: this.autocompleteOptions(),
    };
  });

  public constructor(
    private readonly catalogSearchRepository: CatalogSearchRepositoryService,
    private readonly priceFormatter: PricesFormatter,
  ) {
    super();
  }

  public override ngOnInit(): void {
    super.ngOnInit();

    this.control.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      debounceTime(400),
      distinctUntilChanged(),
      filter(search => this.shouldUpdateOptions(search as string)),
      switchMap(search => this.getUpdatedOptions(search as string)),
      shareReplay(1),
    ).subscribe(options => {
      this.autocompleteOptions.set(options);
      this.control.updateValueAndValidity();
    });
  }

  public ngAfterViewInit(): void {
    this.control.addAsyncValidators(this.validate.bind(this));
  }

  public override validate(): Observable<InputValidationErrors | null> {
    const isValid: boolean = this.autocompleteOptions().some(
      option => option.value === this.control.value,
    );

    return of(isValid ? null : { select: true });
  }

  private shouldUpdateOptions(search?: string): boolean {
    return !!search && search.length > 2 && Boolean(this.control.hasError('select'));
  }

  private getUpdatedOptions(search: string): Observable<AutocompleteOptionModel[]> {
    return this.catalogSearchRepository.getSuggestions(search).pipe(
      map(result => result.suggestions.map(suggestion => {

        const option: AutocompleteOptionModel = {
          label: suggestion.name,
          value: suggestion.sku,
        };

        if (suggestion.prices?.endUserPriceLabel && suggestion.prices?.endUser) {
          const price: string = this.priceFormatter.formatPrice(suggestion.prices.endUser).priceRoundedToHundredth;

          option.suffix = `${suggestion.prices.endUserPriceLabel}: ${price}€`;
        }

        return option;
      })),
    );
  }
}
